import * as React from 'react';
import { Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { formatDate, parseDate } from '../../utils';
import { Dropdown } from '../Dropdown/dropdown';
import { CancelX, Check, Resolved, ThreeDots } from '../Icon/icon';
import './commentForm.scss';
import { Avatar } from '../Avatar/avatar';
import { Overlay } from 'rcd-cpp-ui/ui/loader';
import { Comment } from 'rcd-cpp-ui/ui/icon';
import { MentionsInput, Mention } from 'react-mentions';
import { useSelector } from '../../utils/useSelector';
import { ProductionFSEvent } from '../../enums/ProductionFSEvent';

interface MentionUser {
  id: string;
  display: string;
}
export interface CommentFormProps {
  disabled?: boolean;
  createComment: (
    message: string,
    mentions: any[],
    parentCommentRefId: string
  ) => any;
  updateComment: (refId: string, props: any) => any;
  deleteComment: (refId: string) => any;
  getComments: (refId: string) => any;
  showHideCommentWidget?: () => any;
  shouldShowComments: boolean;
  seeInPlanningLink?: string;
  comments: Comment[];
  searchUsers: (query: any) => Promise<MentionUser[]>;
  searchUserLimit?: number;
  productionFSCapture?:any
}

interface Comment {
  refId: string;
  parentCommentRefId?: string;
  message: string;
  isResolved: boolean;
  childComments?: Comment[];
  createdByTime: Date;
  createdByEmpName: string;
  createdByEmpCd: number;
  modifiedByTime: Date;
  modifiedByEmpName: string;
  modifiedByEmpCd: number;
}

type Option = { name: string; Icon?: any };
export const CommentForm = ({
  createComment,
  updateComment,
  deleteComment,
  getComments,
  disabled,
  showHideCommentWidget,
  shouldShowComments,
  seeInPlanningLink,
  comments,
  searchUsers,
  searchUserLimit = 5,
  productionFSCapture
}: CommentFormProps) => {
  const [newCommentInput, setNewCommentInput] = React.useState('');
  const [mentions, setMentions] = React.useState([]);

  return (
    <div className="comment-container fs-mask">
      {showHideCommentWidget && (
        <a
          onClick={e => {
            showHideCommentWidget();
          }}
          className={`comment-icon ${shouldShowComments ? 'active' : ''}`}
        >
          <Comment
            color={'#009ad7'}
            commented={comments?.length ? true : false}
          />
        </a>
      )}
      {shouldShowComments && (
        <div className="comment-form">
          <div className="header-row">
            <div className="header">Comments</div>
            {showHideCommentWidget && (
              <a
                onClick={e => {
                  showHideCommentWidget();
                }}
                className="close-comments"
              >
                <CancelX />
              </a>
            )}
            {seeInPlanningLink && (
              <a href={seeInPlanningLink} target="_blank" onClick={()=>{
                if(!!productionFSCapture) productionFSCapture(ProductionFSEvent.SeeInPlanningLinkClick)}}>
                See in Planning
              </a>
            )}
          </div>
          <div className="comment-list scrollable">
            {!comments?.length && disabled && <div>No Comments</div>}
            {!disabled && (
              <div className="comment-input">
                <TextAreaWithMentions
                  search={searchUsers}
                  value={newCommentInput}
                  onChange={(e: any, newValue, newPlainTextValue, mentions) => {
                    setNewCommentInput(e.target.value);
                    setMentions(mentions);
                  }}
                  placeholder="Comment"
                />

                {newCommentInput && (
                  <Buttons
                    inputValue={newCommentInput}
                    onCancel={() => {
                      setNewCommentInput('');
                      setMentions([]);
                    }}
                    onSave={() => {
                      createComment(newCommentInput.trim(), mentions, null);
                      setNewCommentInput('');
                      setMentions([]);
                    }}
                    primaryButtonText="Comment"
                    secondaryButtonText="Cancel"
                  />
                )}
              </div>
            )}
            {comments?.map((comment, index) => {
              return (
                <CommentGroup
                  key={comment.refId}
                  comment={comment}
                  disabled={disabled}
                  createComment={createComment}
                  updateComment={updateComment}
                  deleteComment={deleteComment}
                  isLastChild={!comment?.childComments?.length}
                  searchUsers={searchUsers}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface CommentGroupProps {
  comment: Comment;
  disabled?: boolean;
  createComment: (
    message: string,
    mentions: any[],
    parentCommentRefId: string
  ) => any;
  updateComment: (refId: string, props: any) => any;
  deleteComment: (refId: string) => any;
  searchUsers: (query: any) => Promise<MentionUser[]>;
  isChild?: boolean;
  isLastChild?: boolean;
}
enum CommentOption {
  Edit = 'Edit',
  Delete = 'Delete'
}
const CommentGroup = ({
  comment,
  disabled,
  createComment,
  updateComment,
  deleteComment,
  searchUsers,
  isChild,
  isLastChild
}: CommentGroupProps) => {
  const user = useSelector(s => s.app.user);
  const dateTime = parseDate(comment.createdByTime);
  const formattedDateTime = formatDate(dateTime);
  const modifiedDateTime = parseDate(comment.modifiedByTime);
  const childComments = comment.childComments?.length
    ? comment.childComments
    : null;
  const [editCommentValue, setEditCommentValue] = React.useState('');
  const [editedMentions, setEditedMentions] = React.useState([]);
  const [editMode, setEditMode] = React.useState(false);
  const [replyCommentValue, setReplyCommentValue] = React.useState('');
  const [replyMentions, setReplyMentions] = React.useState([]);
  const [shouldConfirmDelete, setShouldConfirmDelete] = React.useState(false);

  const options = [];
  if (user.employeeId == comment.createdByEmpCd && !disabled) {
    options.push({ name: CommentOption.Edit }, { name: CommentOption.Delete });
  }

  return (
    <div
      className={`comment-card ${!isChild ? 'group' : ''}`}
      key={comment.refId}
    >
      <div className="comment-card-header">
        <div className="author-signature">
          <Avatar
            text={comment.createdByEmpName}
            displayText={comment.createdByEmpName
              .split(' ')
              .map(e => e.slice(0, 1))
              .slice(0, 2)}
          />
          <div className="author-details">
            <div className="commenter">{comment.createdByEmpName}</div>
            <div className="date">
              {formattedDateTime + ' | ' + dateTime.toLocaleTimeString()}
              {dateTime.getTime() != modifiedDateTime?.getTime() && (
                <div className="edited">Edited</div>
              )}
            </div>
          </div>
        </div>
        <div className={`card-icon-container`}>
          {!isChild && !comment.isResolved && !disabled && (
            <a
              className="comment-check"
              onClick={e => {
                updateComment(comment.refId, { isResolved: true });
              }}
            >
              <Check />
            </a>
          )}
          {!isChild && comment.isResolved && (
            <div className="comment-checked">
              <Resolved />
            </div>
          )}
          {options.length > 0 && (
            <Dropdown
              position={'right' as any}
              options={options}
              onSelect={e => {
                if (e.name == 'Delete') {
                  setShouldConfirmDelete(true);
                } else if (e.name == 'Edit') {
                  setEditCommentValue(comment.message);
                  setEditMode(true);
                }
              }}
              renderOption={(o: Option) => {
                const I = o.Icon;
                return (
                  <div>
                    <span>{I && <I />}</span>
                    <span className="dd-text" data-txt={o.name}>
                      {o.name}
                    </span>
                  </div>
                );
              }}
            >
              <ThreeDots />
            </Dropdown>
          )}
        </div>
      </div>
      {shouldConfirmDelete && (
        <Overlay opaque={true}>
          <div className="delete-popup">
            <div className="delete-confirmation">
              Delete this comment{`${!isChild ? ' thread' : ''}`}?
            </div>

            <div className="buttons">
              <Secondary
                onClick={() => {
                  deleteComment(comment.refId);
                  setShouldConfirmDelete(false);
                }}
              >
                Delete
              </Secondary>
              <Secondary
                onClick={() => {
                  setShouldConfirmDelete(false);
                }}
              >
                Cancel
              </Secondary>
            </div>
          </div>
        </Overlay>
      )}
      {!editMode && (
        <div className={`message ${isLastChild ? '' : 'group'}`}>
          <RenderMessage message={comment.message} />
        </div>
      )}
      {editMode && !disabled && (
        <>
          <div className="comment-input">
            <TextAreaWithMentions
              search={searchUsers}
              value={editCommentValue}
              onChange={(e: any, newValue, newPlainTextValue, mentions) => {
                setEditCommentValue(e.target.value);
                setEditedMentions(mentions);
              }}
              placeholder={null}
              className="edit"
            />

            <Buttons
              additionalDisableLogic={() => {
                return editCommentValue == comment.message;
              }}
              inputValue={editCommentValue}
              onCancel={() => {
                setEditCommentValue('');
                setEditedMentions([]);
                setEditMode(false);
              }}
              onSave={() => {
                updateComment(comment.refId, {
                  message: editCommentValue.trim(),
                  mentions: editedMentions
                });
                setEditCommentValue('');
                setEditedMentions([]);
                setEditMode(false);
              }}
              primaryButtonText="Save"
              secondaryButtonText="Cancel"
            />
          </div>
        </>
      )}
      {childComments && (
        <div className="child-comment-list">
          {childComments.map((childComment, index) => {
            return (
              <CommentGroup
                key={childComment.refId}
                comment={childComment}
                disabled={disabled}
                createComment={createComment}
                updateComment={updateComment}
                deleteComment={deleteComment}
                searchUsers={searchUsers}
                isChild={true}
                isLastChild={index + 1 == childComments.length}
              />
            );
          })}
        </div>
      )}

      <div className="reply-input">
        {!isChild && !disabled && (
          <TextAreaWithMentions
            search={searchUsers}
            value={replyCommentValue}
            onChange={(e: any, newValue, newPlainTextValue, mentions) => {
              setReplyCommentValue(e.target.value);
              setReplyMentions(mentions);
            }}
            placeholder="Reply"
            className="reply"
          />
        )}

        {replyCommentValue && (
          <Buttons
            inputValue={replyCommentValue}
            onCancel={() => {
              setReplyCommentValue('');
              setReplyMentions([]);
            }}
            onSave={() => {
              createComment(replyCommentValue, replyMentions, comment.refId);
              setReplyCommentValue('');
              setReplyMentions([]);
            }}
            primaryButtonText="Reply"
            secondaryButtonText="Cancel"
          />
        )}
      </div>
    </div>
  );
};

const RenderMessage = ({ message }) => {
  const ret = [];
  const expr = /@\[(.*?)\]\((\d+)\)/;
  let messageToParse = message;

  while (messageToParse?.length) {
    const execResult = expr.exec(messageToParse);
    if (execResult) {
      let beginIndex = 0;
      if (execResult.index > 0) {
        const precedingText = messageToParse.substring(0, execResult.index);
        ret.push(<span>{precedingText}</span>);
        beginIndex = precedingText.length;
      }
      ret.push(<span>@</span>);
      ret.push(<a href={`mailto:${execResult[1]}`}>{execResult[1]}</a>);

      messageToParse = messageToParse.substring(
        beginIndex + execResult[0].length,
        messageToParse.length
      );
    } else {
      ret.push(<span>{messageToParse}</span>);
      messageToParse = null;
    }
  }

  return <>{ret}</>;
};

const TextAreaWithMentions = ({
  value,
  placeholder,
  onChange,
  search,
  className = ''
}) => {
  return (
    <MentionsInput
      className={`mentionWrapper${className ? ' ' + className : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      allowSpaceInQuery={true}
    >
      <Mention
        trigger="@"
        data={async (query, callback) => {
          if (!query) return;
          const res = await search(query);
          callback(res.slice(0, 10));
        }}
        renderSuggestion={s => {
          return (
            <div className="mention-suggestion">
              <Avatar
                text={s.empName}
                displayText={s.empName
                  .split(' ')
                  .map(e => e.slice(0, 1))
                  .slice(0, 2)}
              />
              <div className="name-container">
                <div className="name">{s.empName}</div>
                <div className="email">{s.empEmail}</div>
              </div>
            </div>
          );
        }}
        appendSpaceOnAdd={true}
        displayTransform={(id, display) => `@${display}`}
      />
    </MentionsInput>
  );
};

interface ButtonsProps {
  inputValue: string;
  onCancel: any;
  onSave: any;
  primaryButtonText: string;
  secondaryButtonText: string;
  additionalDisableLogic?: Function;
}

const Buttons = ({
  inputValue,
  onCancel,
  onSave,
  primaryButtonText,
  secondaryButtonText,
  additionalDisableLogic
}: ButtonsProps) => {
  const inputLength = inputValue?.trim()?.length;
  const isDisabled = !!additionalDisableLogic
    ? inputLength == 0 || additionalDisableLogic()
    : inputLength == 0;
  return (
    <div className="buttons">
      <Primary disabled={isDisabled} onClick={onSave}>
        {primaryButtonText}
      </Primary>
      <Secondary onClick={onCancel}>{secondaryButtonText}</Secondary>
    </div>
  );
};
